import axios from 'axios';
import auth from '@/services/auth';
import { assumedOrgState } from '@/services/assumedOrg';
import { isServerOutageError, waitForBackendHealth } from '@/services/backendHealth';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://localhost:8000/api'
});

/**
 * Public member-facing endpoints must not send a JWT.
 * DRF returns 401 when an expired/invalid Bearer token is present — even on AllowAny views.
 */
function isPublicUnauthenticatedApi(url = '') {
  return (
    url.includes('/secure-upload/')
    || url.includes('/advocacy/public/')
    || url.includes('/advocacy/legislative-watch/public/')
    || url.includes('/implementation-pack/')
  );
}

// This interceptor adds the JWT token to every request and sets appropriate Content-Type
api.interceptors.request.use(config => {
  const url = config.url || '';
  const skipAuth = isPublicUnauthenticatedApi(url);

  const token = auth.getToken();
  if (token && !skipAuth) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Inject assumed org/role headers when a Platform Admin is viewing as a client
  // (never on public member pages)
  if (!skipAuth && assumedOrgState.org) {
    config.headers['X-Assumed-Org'] = assumedOrgState.org.id;
    if (assumedOrgState.role) {
      config.headers['X-Assumed-Role'] = assumedOrgState.role;
    }
  }

  // Only set Content-Type to JSON if it's not already set and it's not FormData
  if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
}, error => {
  return Promise.reject(error);
});

// Shared in-flight refresh promise. On page load (or any moment several requests
// expire at once — branding fetch, ticker fetch, etc. all firing together), each
// one used to kick off its own independent /token/refresh/ call. That produced the
// repeated "refresh succeeded, retry still 401" loop seen in practice: N concurrent
// refresh calls racing each other instead of one refresh serving all of them.
// Now every 401 awaits this single shared promise instead of starting a new one.
let refreshPromise = null;

// This interceptor handles token refreshing when a token expires
api.interceptors.response.use(response => {
  return response;
}, async error => {
  const originalRequest = error.config || {};
  const requestUrl = originalRequest.url || '';
  const excludedUrls = ['/token/', '/token/refresh/', '/secure-upload/'];
  const isPublicApi = isPublicUnauthenticatedApi(requestUrl);

  // Check for 401 Unauthorized, not a retry, and not an excluded URL
  // Also exclude public member APIs — never bounce those to login
  if (
    error.response
    && error.response.status === 401
    && !originalRequest._retry
    && !excludedUrls.some((u) => requestUrl.includes(u))
    && !isPublicApi
  ) {
    originalRequest._retry = true;
    try {
      if (!refreshPromise) {
        console.log('Attempting to refresh token...');
        refreshPromise = auth.refreshToken().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
      console.log('Token refreshed, retrying original request.');
      return api(originalRequest);
    } catch (refreshError) {
      console.error('Unable to refresh token.', refreshError);
      auth.logout();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    }
  }

  const isHealthCheck = requestUrl.includes('/health/');
  if (
    isServerOutageError(error)
    && originalRequest
    && !originalRequest._outageRetry
    && !isHealthCheck
    && !isPublicApi
  ) {
    originalRequest._outageRetry = true;
    try {
      await waitForBackendHealth();
      return api(originalRequest);
    } catch (recoveryError) {
      return Promise.reject(error);
    }
  }

  return Promise.reject(error);
});

export default api;
